/**
 * ContinuityAssetsPanel — the "Your Assets" section on the Vault screen.
 *
 * Only rendered for Continuity System users. Fetches the user's
 * investments once and renders each as a ContinuityAssetCard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContinuityAssetCard } from './ContinuityAssetCard';
import { ContinuityAssetEditor } from './ContinuityAssetEditor';
import { computeContinuityStatus, CONTINUITY_LEVEL } from './continuityStatus';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function loadTierAndInvestments() {
  const [me, inv] = await Promise.all([
    fetch(`${BACKEND_URL}/api/auth/me`, { headers: authHeader() }).then((r) => r.json()),
    fetch(`${BACKEND_URL}/api/investments`, { headers: authHeader() }).then((r) => r.json()),
  ]);
  return { tier: me.tier || 'premium_portfolio', investments: Array.isArray(inv) ? inv : (inv.investments || []) };
}

async function loadDocCounts() {
  const r = await fetch(`${BACKEND_URL}/api/continuity-documents/counts`, { headers: authHeader() });
  if (!r.ok) return {};
  const { counts } = await r.json();
  return counts || {};
}

export function ContinuityAssetsPanel({ onCountChange }) {
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState('premium_portfolio');
  const [investments, setInvestments] = useState([]);
  const [docCounts, setDocCounts] = useState({});
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { tier: t, investments: inv } = await loadTierAndInvestments();
      setTier(t);
      setInvestments(inv);
      if (t === 'premium_continuity') {
        const counts = await loadDocCounts();
        setDocCounts(counts);
      }
    } catch (e) {
      logger.error('continuity assets load failed', e);
      toast.error('Could not load your assets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Include both open and closed positions — a Trusted Contact would want
  // to know about every investment the user has recorded, not just the
  // ones still open. (Founder decision, Feb 2026.)
  const active = investments;

  // Tell the parent panel how many cards are being rendered so it can
  // paint the count into the sub-tab label without duplicating the fetch.
  useEffect(() => {
    if (onCountChange) onCountChange(active.length);
  }, [active.length, onCountChange]);

  // Fire the cross-tab jump event to go to the main Add Investments tab.
  // Both experiences share the same three-card entry point (Manual /
  // Batch / Import from institution), so we route here rather than
  // duplicating those flows inside the Continuity System tab.
  // (Detail must match the top-level Tabs `value` — that tab is called
  // "add", not "add-investments".)
  const goToAddInvestments = () => {
    window.dispatchEvent(new CustomEvent('mif:go-to-tab', { detail: 'add' }));
  };

  // Roll-up counter — one calm line so Continuity users can glance and know
  // where they are without any urgency or numbers-heavy dashboard vibe.
  const tallies = useMemo(() => {
    const t = { complete: 0, in_progress: 0, at_risk: 0 };
    active.forEach((inv) => {
      const { level } = computeContinuityStatus(inv);
      if (level === CONTINUITY_LEVEL.COMPLETE) t.complete += 1;
      else if (level === CONTINUITY_LEVEL.IN_PROGRESS) t.in_progress += 1;
      else t.at_risk += 1;
    });
    return t;
  }, [active]);

  if (loading) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading your assets…
      </div>
    );
  }

  // Portfolio-tier users never reach this component (the Dashboard gates the
  // Continuity System tab with EstateVaultUpgradeLock). Guard anyway so the panel
  // renders nothing if it somehow mounts under the wrong tier.
  if (tier !== 'premium_continuity') return null;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="continuity-assets-panel">
      {/* Contextual welcome — live count from the same source that drives
          the cards below, so it can never drift out of sync. */}
      <div
        className="text-sm text-emerald-900 bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-2 mb-3"
        data-testid="portfolio-investments-welcome"
      >
        {active.length > 0
          ? <>Your <strong>{active.length}</strong> {active.length === 1 ? 'investment is' : 'investments are'} already here &mdash; add more below.</>
          : <>No investments yet &mdash; add your first one below.</>
        }
      </div>
      <div className="flex items-start gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-emerald-700 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-slate-800">Portfolio Investments</h4>
          <p className="text-xs text-slate-500 max-w-2xl mt-0.5">
            Here&rsquo;s each of your investments the way a Trusted Contact would read them &mdash; what it is, where it&rsquo;s held, how much, and where the paperwork lives. Fill these in at your own pace; the label updates on its own.
          </p>
        </div>
      </div>

      {/* Completeness — a proper progress card rather than three inline
          numbers. This is the single most emotionally-loaded piece of
          info on the page and deserves visual weight. */}
      {active.length > 0 && (() => {
        const done = tallies.complete;
        const partial = tallies.in_progress;
        const todo = tallies.at_risk;
        const pctDone = Math.round((done / active.length) * 100);
        const pctPartial = Math.round((partial / active.length) * 100);
        return (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-white p-3" data-testid="continuity-rollup">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-xs font-medium text-slate-700">
                Completeness &middot; <span className="text-emerald-800">{done} of {active.length} done</span>
                {partial > 0 && <span className="text-slate-500"> &middot; {partial} in progress</span>}
                {todo > 0 && <span className="text-slate-500"> &middot; {todo} to start</span>}
              </div>
              <div className="text-xs font-semibold text-emerald-700 tabular-nums">{pctDone}%</div>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden flex" role="progressbar" aria-valuenow={pctDone} aria-valuemin="0" aria-valuemax="100">
              <div className="bg-emerald-500 h-full" style={{ width: `${pctDone}%` }} />
              <div className="bg-amber-300 h-full" style={{ width: `${pctPartial}%` }} />
            </div>
          </div>
        );
      })()}

      {/* Primary CTA — front-and-centre when there's anything to fill in.
          Once every asset is complete, this collapses to the small link at
          the bottom of the section so the page stays quiet. */}
      {active.length > 0 && tallies.complete < active.length && (
        <button
          type="button"
          onClick={goToAddInvestments}
          className="w-full mb-3 rounded-lg border border-dashed border-emerald-300 bg-white hover:bg-emerald-50/50 transition-colors p-4 text-left flex items-center gap-3"
          data-testid="ca-add-primary-cta"
        >
          <div className="p-2 rounded-full bg-emerald-100 text-emerald-700"><Plus className="w-4 h-4" /></div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Add Investments</div>
            <div className="text-xs text-slate-500">Add one at a time, in a batch, or import directly from your bank or asset manager.</div>
          </div>
          <span className="text-xs text-emerald-700 font-medium">Start &rarr;</span>
        </button>
      )}

      {active.length === 0 ? (
        <div className="border border-dashed border-emerald-300 bg-white rounded-lg p-6 text-center" data-testid="continuity-empty-state">
          <ShieldCheck className="w-8 h-8 text-emerald-700 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-800 mb-1">Start with your first investment.</p>
          <p className="text-xs text-slate-500 max-w-md mx-auto mb-3">
            Tell us what it is, where it&rsquo;s held, how much it&rsquo;s worth, and where the paperwork lives. It becomes part of your Portfolio right away, and a Trusted Contact would be able to make sense of it later.
          </p>
          <Button
            size="sm"
            onClick={goToAddInvestments}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="ca-add-first-asset-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Investments
          </Button>
          <p className="text-[11px] text-slate-400 mt-3">
            Add one at a time, in a batch, or import directly from your bank or asset manager &mdash; the same options as the <strong>Add Investments</strong> tab.
          </p>
        </div>
      ) : (
        <div className="grid gap-2">
          {active.map((inv) => (
            <ContinuityAssetCard
              key={inv.investment_id}
              investment={inv}
              docCount={docCounts[inv.investment_id] || 0}
              onEdit={(target) => { setEditing(target); setEditorOpen(true); }}
            />
          ))}
        </div>
      )}

      {/* Small link — always available for the everything-complete state. */}
      {active.length > 0 && tallies.complete === active.length && (
        <div className="mt-3 text-right">
          <Button size="sm" variant="ghost" onClick={goToAddInvestments} data-testid="ca-add-asset-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Investments
          </Button>
        </div>
      )}

      <ContinuityAssetEditor
        investment={editing}
        open={editorOpen}
        onOpenChange={(v) => {
          setEditorOpen(v);
          // When the modal closes, refresh so any uploaded/deleted documents
          // are reflected on the paperclip badge and roll-up counter.
          if (!v) reload();
        }}
        onSaved={() => reload()}
      />
    </div>
  );
}
